import React, { useState, useEffect, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';
import api from './services/api';
import { Container, Navbar, Nav, Button, Badge, NavDropdown } from 'react-bootstrap';
import { FaUserShield, FaUserTie, FaSignInAlt } from 'react-icons/fa';

// Components
import Home from './components/Home'; 
import Login from './components/Login';
import Register from './components/Register'; 
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Pricing from './components/Pricing';
import BotManagement from './components/BotManagement';
import AdminDashboard from './components/AdminDashboard';
import Products from './components/Products';
import NewsFeed from './components/NewsFeed';
import TermsOfService from './components/TermsOfService'; 
import AgencyLanding from './components/AgencyLanding';
import AgencyDashboard from './components/AgencyDashboard';
import TransactionHistory from './components/TransactionHistory';
import ResourceHub from './components/ResourceHub';

import './App.css';

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentView, setCurrentView] = useState('home'); 
    const [userData, setUserData] = useState(null);
    const [userRole, setUserRole] = useState('user'); 
    const [orderToPay, setOrderToPay] = useState(null);
    const [isPrivilegeMode, setIsPrivilegeMode] = useState(false);
    const [preSelectedBot, setPreSelectedBot] = useState(null);
    const [resetToken, setResetToken] = useState(null); 
    const [navExpanded, setNavExpanded] = useState(false);

    const navigateTo = (view) => {
        if (['register', 'home', 'login', 'forgot-password'].includes(view)) {
            setResetToken(null);
        }
        setCurrentView(view);
        setNavExpanded(false);
        window.scrollTo(0, 0);
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => !isAuthenticated && (currentView === 'home' ? navigateTo('products') : currentView === 'products' && navigateTo('news')),
        onSwipedRight: () => !isAuthenticated && (currentView === 'news' ? navigateTo('products') : currentView === 'products' && navigateTo('home')),
        trackMouse: true
    });

    useEffect(() => {
        const path = window.location.pathname;
        const urlParams = new URLSearchParams(window.location.search);
        const ref = urlParams.get('ref');

        if (ref) {
            localStorage.setItem('referred_by_agency', ref.toLowerCase().trim());
            window.agencySlug = ref;
            setCurrentView('agency_landing'); 
        } else if (path.includes('/reset-password/')) {
            const token = path.split('/reset-password/')[1];
            if (token) {
                setResetToken(token);
                setCurrentView('reset-password');
            }
        } else if (path.startsWith('/agency/') || path.startsWith('/landing/')) {
            const slug = path.split('/')[2];
            if (slug) {
                localStorage.setItem('referred_by_agency', slug.toLowerCase().trim());
                window.agencySlug = slug;
                setCurrentView('agency_landing');
            }
        }
    }, []);

    const handleLogout = useCallback(() => {
        localStorage.clear();
        setIsAuthenticated(false);
        setUserData(null);
        setUserRole('user');
        setCurrentView('home'); 
    }, []);

    const fetchUserInfo = useCallback(async () => {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;
        try {
            const res = await api.get('/auth/me'); 
            if (res.data.success) {
                const user = res.data.user;
                setIsAuthenticated(true);
                setUserData(user);
                setUserRole(user.role || 'user');
                if (['home', 'login', 'register'].includes(currentView)) {
                    if (user.role === 'admin') setCurrentView('admin_dashboard'); 
                    else if (user.role === 'agency') setCurrentView('agency_dashboard');
                    else setCurrentView(localStorage.getItem('pending_privilege') === 'true' ? 'pricing' : 'products');
                }
            }
        } catch (err) { handleLogout(); }
    }, [handleLogout, currentView]);

    useEffect(() => { fetchUserInfo(); }, [fetchUserInfo]);

    const handleLogin = (token, user) => {
        localStorage.setItem('jwtToken', token);
        setIsAuthenticated(true);
        setUserData(user);
        setUserRole(user.role || 'user');
        if (user.role === 'admin') setCurrentView('admin_dashboard');
        else if (user.role === 'agency') setCurrentView('agency_dashboard');
        else setCurrentView('products');
    };

    const renderNavItems = () => {
        if (!isAuthenticated) return [
            { view: 'home', name: 'TRANG CHỦ' }, { view: 'products', name: 'SẢN PHẨM BOT' },
            { view: 'news', name: 'HƯỚNG DẪN' }, { view: 'resource_hub', name: 'NGUỒN LỰC' }, { view: 'terms', name: 'ĐIỀU KHOẢN' }
        ];
        if (userRole === 'admin') return [];
        const items = [{ view: 'products', name: 'SẢN PHẨM BOT' }, { view: 'pricing', name: 'ĐĂNG KÍ SỞ HỮU BOT' }, { view: 'bots', name: 'CÀI ĐẶT BOT' }, { view: 'transaction-history', name: 'THANH TOÁN' }, { view: 'news', name: 'HƯỚNG DẪN' }, { view: 'terms', name: 'ĐIỀU KHOẢN' }];
        if (userRole !== 'admin') items.push({ view: 'resource_hub', name: 'NGUỒN LỰC' });
        return items;
    };

    const renderView = () => {
        if (currentView === 'agency_landing') return <AgencyLanding slug={window.agencySlug} setCurrentView={navigateTo} />;
        if (!isAuthenticated) {
            switch (currentView) {
                case 'home': return <Home setCurrentView={navigateTo} setIsPrivilegeMode={setIsPrivilegeMode} />;
                case 'products': return <Products setCurrentView={navigateTo} isAuthenticated={false} />;
                case 'news': return <NewsFeed />;
                case 'terms': return <TermsOfService />;
                case 'resource_hub': return <ResourceHub setCurrentView={navigateTo} />;
                case 'register': return <Register onLogin={handleLogin} setCurrentView={navigateTo} />;
                case 'login': return <Login onLogin={handleLogin} setCurrentView={navigateTo} />;
                case 'forgot-password': return <ForgotPassword setCurrentView={navigateTo} />;
                case 'reset-password': return <ResetPassword token={resetToken} setCurrentView={navigateTo} />;
                default: return <Products setCurrentView={navigateTo} />;
            }
        }
        if (userRole === 'admin') return <AdminDashboard />;
        switch (currentView) {
            case 'agency_dashboard': return <AgencyDashboard setCurrentView={navigateTo} />;
            case 'products': return <Products setCurrentView={navigateTo} isAuthenticated={true} />;
            case 'pricing': return <Pricing setCurrentView={navigateTo} setOrderToPay={setOrderToPay} isPrivilegeMode={isPrivilegeMode} preSelectedBot={preSelectedBot} />;
            case 'bots': return <BotManagement user={userData} />;
            case 'transaction-history': return <TransactionHistory setCurrentView={navigateTo} />;
            case 'resource_hub': return <ResourceHub setCurrentView={navigateTo} />;
            default: return userRole === 'agency' ? <AgencyDashboard setCurrentView={navigateTo} /> : <Products setCurrentView={navigateTo} />;
        }
    };

    return (
        <div {...swipeHandlers} style={{ backgroundColor: '#020617', minHeight: '100vh', color: 'white' }}>
            <Navbar expanded={navExpanded} onToggle={setNavExpanded} bg="dark" variant="dark" expand="xl" className="border-bottom border-warning sticky-top">
                <Container fluid>
                    <Navbar.Brand onClick={() => navigateTo('home')} style={{ color: '#ffc107', cursor: 'pointer', fontWeight: '900' }}>⚡ NEXBOT</Navbar.Brand>
                    <Navbar.Toggle />
                    <Navbar.Collapse>
                        <Nav className="mx-auto">
                            {renderNavItems().map(item => (
                                <Nav.Link key={item.view} onClick={() => navigateTo(item.view)} className={currentView === item.view ? 'text-warning' : 'text-light'}>{item.name}</Nav.Link>
                            ))}
                        </Nav>
                        <Nav>
                            {isAuthenticated ? <Button variant="outline-danger" onClick={handleLogout}>ĐĂNG XUẤT</Button> : <Button variant="warning" onClick={() => navigateTo('login')}><FaSignInAlt /> ĐĂNG NHẬP</Button>}
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            {renderView()}
        </div>
    );
}

export default App;